import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';

import { Form, Select, Input, Divider, Card, Typography, Switch } from 'antd';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';

import { FeesContext } from 'components/landlord_app/payments/LandlordFees';
import FeeEventsForm from './FeeEventsForm';
import { getTranslatedString } from 'helpers/i18n';

const { TextArea } = Input;
const requiredField = {
  required: true,
  message: getTranslatedString('required'),
};

const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const MerchantAccountTypeSelect = () => {
  const options = [
    { value: 'general', label: 'General' },
    { value: 'deposit', label: 'Deposit' },
  ];

  return (
    <Spacer mb={1}>
      <Form.Item
        name="merchant_account_type"
        label="Bank account type"
        hasFeedback
        rules={[requiredField]}
      >
        <Select options={options} />
      </Form.Item>
    </Spacer>
  );
};

const ChargeCodeSelect = () => {
  const feeData = useContext(FeesContext);
  const { chargeCodes } = feeData;

  const options = useMemo(() => {
    return chargeCodes.map((c) => {
      return { value: c.id, label: c.code };
    });
  }, [chargeCodes]);

  return (
    <Spacer mb={1}>
      <Form.Item name="charge_code_id" label="Charge code" hasFeedback rules={[requiredField]}>
        <Select
          showSearch
          options={options}
          optionFilterProp="label"
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
    </Spacer>
  );
};

const Configuration = () => (
  <Row gutter={16}>
    <Col xs={24} sm={20} md={20} lg={12} xl={12}>
      <Card>
        <StyledSectionContainer>
          <Typography.Title level={4}>Configuration</Typography.Title>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="status" hidden>
            <Input />
          </Form.Item>

          <Spacer mb={1}>
            <Form.Item
              name="internal_name"
              label="Internal Name"
              hasFeedback
              rules={[requiredField]}
            >
              <Input />
            </Form.Item>
          </Spacer>

          <Spacer mb={1}>
            <Form.Item
              name="internal_description"
              label="Internal Description"
              hasFeedback
              rules={[requiredField]}
            >
              <TextArea rows={4} />
            </Form.Item>
          </Spacer>

          <Spacer mb={1}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.externally_visible !== currentValues.externally_visible
              }
            >
              {({ getFieldValue }) => {
                const hasExternalData =
                  !getFieldValue('id') ||
                  (getFieldValue('external_name') && getFieldValue('external_description'));

                return (
                  <Form.Item
                    initialValue={hasExternalData}
                    name="externally_visible"
                    label="Externally visible"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Spacer>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.externally_visible !== currentValues.externally_visible
            }
          >
            {({ getFieldValue }) => {
              return getFieldValue('externally_visible') ? (
                <>
                  <Spacer mb={1}>
                    <Form.Item
                      name={'external_name'}
                      label="External Name"
                      hasFeedback
                      rules={[requiredField]}
                    >
                      <Input />
                    </Form.Item>
                  </Spacer>

                  <Spacer mb={1}>
                    <Form.Item
                      name={'external_description'}
                      label="External Description"
                      hasFeedback
                      rules={[requiredField]}
                    >
                      <TextArea rows={4} />
                    </Form.Item>
                  </Spacer>
                </>
              ) : null;
            }}
          </Form.Item>

          <MerchantAccountTypeSelect />
          <ChargeCodeSelect />
        </StyledSectionContainer>

        <Divider />

        <StyledSectionContainer>
          <FeeEventsForm />
        </StyledSectionContainer>
      </Card>
    </Col>
  </Row>
);

export default Configuration;
